import { useMemo } from "react"

import { ATTENDANCE_STATUSES } from "#constants/globalConstants"
import { AttendanceClassSessions } from "#customTypes/types"

/**
 * Hook to calculate session selection state (newly selected vs dropped)
 * for use in Add X to Class forms
 */
export const useSessionSelectionState = (
  selectedSessions: boolean[],
  attendanceForClass: AttendanceClassSessions
) => {
  // Calculate which sessions are newly selected (were NOT_ENROLLED or INVALID before but are now selected)
  const newlySelectedSessions = useMemo(() => {
    return selectedSessions.map((isSelected, i) => {
      if (!isSelected) return false
      const previousStatus = attendanceForClass[i]?.status
      return (
        previousStatus === ATTENDANCE_STATUSES.NOT_ENROLLED ||
        previousStatus === ATTENDANCE_STATUSES.INVALID
      )
    })
  }, [selectedSessions, attendanceForClass])

  // Calculate which sessions are being dropped (were previously enrolled but are now not selected)
  const droppedSessions = useMemo(() => {
    return selectedSessions.map((isSelected, i) => {
      if (isSelected) return false // Can't be dropped if still selected
      const previousStatus = attendanceForClass[i]?.status
      // Was previously enrolled (only count regular enrollment statuses)
      return (
        previousStatus === ATTENDANCE_STATUSES.ENROLLED ||
        previousStatus === ATTENDANCE_STATUSES.PRESENT ||
        previousStatus === ATTENDANCE_STATUSES.ABSENT ||
        previousStatus === ATTENDANCE_STATUSES.TARDY ||
        previousStatus === ATTENDANCE_STATUSES.PLANNED_ABSENCE
      )
    })
  }, [selectedSessions, attendanceForClass])

  // Calculate count of newly added sessions
  const newlyAddedSessionCount = useMemo(() => {
    return newlySelectedSessions.filter(Boolean).length
  }, [newlySelectedSessions])

  // Calculate count of dropped sessions
  const droppedSessionCount = useMemo(() => {
    return droppedSessions.filter(Boolean).length
  }, [droppedSessions])

  // Calculate net session count (added - removed)
  const netSessionCount = useMemo(() => {
    return newlyAddedSessionCount - droppedSessionCount
  }, [newlyAddedSessionCount, droppedSessionCount])

  return {
    newlySelectedSessions,
    droppedSessions,
    newlyAddedSessionCount,
    droppedSessionCount,
    netSessionCount,
  }
}
